/**
 * features/invoices/InvoiceEditor.tsx
 * ------------------------------------------------------------------
 * Create/edit a draft invoice, or view an issued one (read-only with
 * a Void action). Totals compute live via money.ts under the current
 * VAT snapshot; the VAT column only appears once the company is
 * VAT-registered (dormant until then).
 * ------------------------------------------------------------------
 */
import { useMemo, useState } from 'react';
import { X, Plus, Trash2, Ban, Download, Mail, FileText, Receipt } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useClients } from '@/features/clients/useClients';
import { buildLineItems, computeTotals, formatPula } from '@/lib/money';
import type { Invoice, VatTreatment } from '@/types';
import { DEFAULT_COMPANY_SETTINGS } from '@/types';
import {
  clientSnapshot,
  saveDraft,
  issueInvoice,
  voidInvoice,
  deleteDraft,
  type InvoicePayload,
} from './invoicesWrite';
import { Button, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { glassSurface } from '@/components/ui/glass';
import { downloadInvoicePdf } from '@/lib/invoicePdf';
import { SendInvoiceModal } from './SendInvoiceModal';
import { RecordPaymentModal } from '@/features/payments/RecordPaymentModal';

interface Row {
  id: string;
  description: string;
  quantity: string;
  unitPrice: string;
  vatTreatment: VatTreatment;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}
function plusDaysISO(base: string, days: number): string {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function newRow(): Row {
  return { id: crypto.randomUUID(), description: '', quantity: '1', unitPrice: '', vatTreatment: 'standard' };
}

export function InvoiceEditor({ invoice, onClose }: { invoice: Invoice | null; onClose: () => void }) {
  const { user } = useAuth();
  const { currentVatSnapshot, settings } = useSettings();
  const { clients } = useClients();
  const vat = currentVatSnapshot();

  const readOnly = invoice != null && invoice.status !== 'draft';
  const canVoid = invoice != null && (invoice.status === 'issued' || invoice.status === 'part_paid');

  const [clientId, setClientId] = useState(invoice?.clientId ?? '');
  const [issueDate, setIssueDate] = useState(invoice?.issueDate ?? todayISO());
  const [dueDate, setDueDate] = useState(invoice?.dueDate ?? plusDaysISO(todayISO(), 14));
  const [notes, setNotes] = useState(invoice?.notes ?? '');
  const [rows, setRows] = useState<Row[]>(
    invoice
      ? invoice.lineItems.map((l) => ({
          id: l.id,
          description: l.description,
          quantity: String(l.quantity),
          unitPrice: String(l.unitPrice),
          vatTreatment: l.vatTreatment,
        }))
      : [newRow()],
  );
  const [busy, setBusy] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [error, setError] = useState('');

  const built = useMemo(
    () =>
      buildLineItems(
        rows.map((r) => ({
          id: r.id,
          description: r.description.trim(),
          quantity: Number(r.quantity) || 0,
          unitPrice: Number(r.unitPrice) || 0,
          vatTreatment: r.vatTreatment,
        })),
        vat,
      ),
    [rows, vat],
  );
  const totals = useMemo(() => computeTotals(built), [built]);

  const activeClients = clients.filter((c) => c.active || c.id === clientId);
  const selectedClient = clients.find((c) => c.id === clientId);

  const valid =
    clientId.trim().length > 0 &&
    built.some((l) => l.description.length > 0 && l.quantity > 0);

  function setRow(i: number, key: keyof Row, value: string) {
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [key]: value } : r)));
  }

  function buildPayload(): InvoicePayload | null {
    if (!selectedClient && !invoice?.client) return null;
    const client = selectedClient ? clientSnapshot(selectedClient) : invoice!.client;
    return {
      clientId,
      client,
      issueDate,
      dueDate,
      lineItems: built.filter((l) => l.description.length > 0),
      totals: computeTotals(built.filter((l) => l.description.length > 0)),
      vat,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };
  }

  const actor = user?.email ?? user?.uid ?? 'unknown';

  async function doSaveDraft() {
    const payload = buildPayload();
    if (!payload) {
      setError('Choose a client.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await saveDraft(payload, actor, invoice?.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the draft.');
    } finally {
      setBusy(false);
    }
  }

  async function doIssue() {
    const payload = buildPayload();
    if (!payload) {
      setError('Choose a client.');
      return;
    }
    if (
      !window.confirm(
        'Issue this invoice? It will be assigned a permanent number and can no longer be edited (only voided or credited).',
      )
    )
      return;
    setBusy(true);
    setError('');
    try {
      await issueInvoice(payload, actor, invoice?.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not issue the invoice.');
    } finally {
      setBusy(false);
    }
  }

  async function doVoid() {
    if (!invoice) return;
    if (!window.confirm(`Void ${invoice.number}? The number is retained and never reused.`)) return;
    setBusy(true);
    try {
      await voidInvoice(invoice.id);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  async function doDiscard() {
    if (!invoice) return;
    if (!window.confirm('Discard this draft? This cannot be undone.')) return;
    setBusy(true);
    try {
      await deleteDraft(invoice.id);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  const title = invoice
    ? readOnly
      ? `Invoice ${invoice.number}`
      : 'Edit draft invoice'
    : 'New invoice';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-md">
      <div className="my-8 w-full max-w-3xl">
        <div className={glassSurface('p-5 space-y-4')}>
          <div className="-mx-5 -mt-5 mb-1 flex items-center justify-between rounded-t-card bg-brand px-5 py-3.5">
            <div className="flex items-center gap-2 text-white">
              <FileText size={18} />
              <h2 className="text-base font-semibold text-white">{title}</h2>
              {invoice && (
                <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold text-white">
                  {invoice.status.replace('_', ' ')}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1 text-white/80 transition hover:bg-white/15 hover:text-white"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Client + dates */}
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Client">
              {readOnly ? (
                <Input value={invoice!.client.name} disabled />
              ) : (
                <Select value={clientId} onChange={(e) => setClientId(e.target.value)}>
                  <option value="">Select a client…</option>
                  {activeClients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Issue date">
              <Input type="date" value={issueDate} disabled={readOnly} onChange={(e) => setIssueDate(e.target.value)} />
            </Field>
            <Field label="Due date">
              <Input type="date" value={dueDate} disabled={readOnly} onChange={(e) => setDueDate(e.target.value)} />
            </Field>
          </div>

          {/* Line items */}
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-primaryLight dark:text-gray-400">
                Line items
              </span>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => setRows((rs) => [...rs, newRow()])}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline"
                >
                  <Plus size={13} /> Add line
                </button>
              )}
            </div>

            <div className="space-y-2">
              {rows.map((r, i) => {
                const line = built[i];
                return (
                  <div
                    key={r.id}
                    className="grid grid-cols-[1fr_64px_96px_auto] items-center gap-2 rounded-xl border border-black/5 p-2 dark:border-white/10"
                  >
                    <Input
                      placeholder="Description"
                      value={r.description}
                      disabled={readOnly}
                      onChange={(e) => setRow(i, 'description', e.target.value)}
                    />
                    <Input
                      inputMode="decimal"
                      placeholder="Qty"
                      value={r.quantity}
                      disabled={readOnly}
                      onChange={(e) => setRow(i, 'quantity', e.target.value)}
                      className="text-right"
                    />
                    <Input
                      inputMode="decimal"
                      placeholder="Unit price"
                      value={r.unitPrice}
                      disabled={readOnly}
                      onChange={(e) => setRow(i, 'unitPrice', e.target.value)}
                      className="text-right"
                    />
                    <div className="flex items-center gap-2">
                      <span className="w-24 text-right text-sm font-medium text-primary dark:text-gray-200">
                        {formatPula(line?.lineTotal ?? 0)}
                      </span>
                      {!readOnly && rows.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                          className="rounded-lg p-1.5 text-gray-400 hover:bg-black/5 hover:text-red-600 dark:hover:bg-white/5"
                          aria-label="Remove line"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                    {vat.registered && !readOnly && (
                      <div className="col-span-4">
                        <Select
                          value={r.vatTreatment}
                          onChange={(e) => setRow(i, 'vatTreatment', e.target.value)}
                          className="text-xs"
                        >
                          <option value="standard">Standard ({vat.rate}% VAT)</option>
                          <option value="zero">Zero-rated</option>
                          <option value="exempt">Exempt</option>
                        </Select>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between text-primaryLight dark:text-gray-400">
                <span>Subtotal</span>
                <span>{formatPula(totals.subtotal)}</span>
              </div>
              {vat.registered && (
                <div className="flex justify-between text-primaryLight dark:text-gray-400">
                  <span>VAT ({vat.rate}%)</span>
                  <span>{formatPula(totals.vatTotal)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-black/10 pt-1 text-base font-bold text-primary dark:border-white/10 dark:text-gray-100">
                <span>Total</span>
                <span>{formatPula(totals.total)}</span>
              </div>
            </div>
          </div>

          <Field label="Notes">
            {readOnly ? (
              <Textarea value={invoice!.notes ?? ''} rows={2} disabled />
            ) : (
              <Textarea value={notes} rows={2} onChange={(e) => setNotes(e.target.value)} />
            )}
          </Field>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          {/* Footer */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              {!readOnly && invoice && (
                <Button variant="ghost" onClick={doDiscard} disabled={busy}>
                  Discard draft
                </Button>
              )}
              {canVoid && (
                <Button variant="danger" icon={<Ban size={16} />} onClick={doVoid} loading={busy}>
                  Void invoice
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {readOnly && invoice && (
                <>
                  <Button
                    variant="secondary"
                    icon={<Download size={16} />}
                    onClick={() => downloadInvoicePdf(invoice, settings ?? DEFAULT_COMPANY_SETTINGS)}
                  >
                    PDF
                  </Button>
                  <Button variant="secondary" icon={<Mail size={16} />} onClick={() => setShowSend(true)}>
                    Email
                  </Button>
                  {(invoice.status === 'issued' || invoice.status === 'part_paid') && invoice.balance > 0 && (
                    <Button icon={<Receipt size={16} />} onClick={() => setShowPay(true)}>
                      Record payment
                    </Button>
                  )}
                </>
              )}
              <Button variant="secondary" onClick={onClose}>
                {readOnly ? 'Close' : 'Cancel'}
              </Button>
              {!readOnly && (
                <>
                  <Button variant="secondary" onClick={doSaveDraft} loading={busy} disabled={!valid}>
                    Save draft
                  </Button>
                  <Button onClick={doIssue} loading={busy} disabled={!valid}>
                    Issue invoice
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {showSend && invoice && (
        <SendInvoiceModal
          invoice={invoice}
          company={settings ?? DEFAULT_COMPANY_SETTINGS}
          onClose={() => setShowSend(false)}
        />
      )}

      {showPay && invoice && (
        <RecordPaymentModal
          invoice={invoice}
          onClose={() => setShowPay(false)}
          onRecorded={() => {
            setShowPay(false);
            onClose();
          }}
        />
      )}
    </div>
  );
}
